import { ActiveSelection } from 'types/ps/selections'
import { RegionType } from 'types/region-type'

import {
  apiFileDownload,
  useInfiniteQueryApi,
  useMutationApi,
  useQueryApi,
} from 'utils/api'
import { baseAPIPathGuruKS } from 'configs/api'

const basePath = `${baseAPIPathGuruKS}/v1/ps/selections`

interface ParamsGetActiveSelection {
  regionId: string
  regionType: RegionType
}

export interface ResponseGetActiveSelection {
  selection: null | ActiveSelection
}

export const useAPIGetActiveSelection = (params: ParamsGetActiveSelection) =>
  useQueryApi<ResponseGetActiveSelection>(`${basePath}/active`, {
    axiosConfig: {
      params,
    },
    queryConfig: {
      staleTime: 1000,
    },
  })

interface ResponseSuccessStartSelection {
  selectionId: string
}

interface RequestBodyStartSelection {
  regionId: string
  regionType: RegionType
}

export const useAPIStartSelection = () =>
  useMutationApi<
    ResponseSuccessStartSelection,
    Error,
    RequestBodyStartSelection
  >(basePath, {
    axiosConfig: {
      method: 'POST',
    },
  })

interface ResponseSuccessUploadSuratPersetujuanFormasi {
  persetujuanFileUrl: string
  selectionId: string
  persetujuanFileName: string
}

export const useAPIUploadSuratPersetujuanFormasi = () =>
  useMutationApi<ResponseSuccessUploadSuratPersetujuanFormasi, Error, FormData>(
    `${basePath}/persetujuan-formasi`,
    {
      axiosConfig: {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

interface ResponseSuccessCreateSelection {
  message: string
}

export const useAPICreatePSSelection = (selectionId: ActiveSelection['id']) =>
  useMutationApi<ResponseSuccessCreateSelection, Error, FormData>(
    `${basePath}/${selectionId}/start`,
    {
      axiosConfig: {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

export interface ParamsGetSelectionOverview {
  regionId: string
  regionType: RegionType
}

export interface ResponseSuccessGetSelectionOverview {
  totalSelected: number
  totalMudaSelected: number
  totalMadyaSelected: number
  totalUtamaSelected: number
}

export const useAPIGetSelectionOverview = (
  selectionId: ActiveSelection['id'],
  params: ParamsGetSelectionOverview
) =>
  useQueryApi<ResponseSuccessGetSelectionOverview>(
    `${basePath}/${selectionId}/overview`,
    {
      axiosConfig: {
        params,
      },
      queryConfig: {
        enabled: !!selectionId,
      },
    }
  )

export interface ParamsGetSelectionCandidates {
  page: number
  pageSize: number
  candidateStatusFilter: 'ALL' | 'SELECTED' | 'NOT_SELECTED'
}

export interface PSSelectionCandidate {
  nama: string
  sekolah: string
  jabatan: string
  jenjangJabatan: string
  kualifikasiAkademik: string
  ptkId: string
  isSelected: boolean
  candidateId: string
  age: string
}
export interface ResponseGetPSSelectionCandidates {
  data: PSSelectionCandidate[]
  meta: {
    page: number
    pageSize: number
    totalSelected: number
    totalData: number
  }
}

export const useAPIGetSelectionCandidates = (
  selectionId: ActiveSelection['id'],
  params: ParamsGetSelectionCandidates
) =>
  useQueryApi<ResponseGetPSSelectionCandidates>(
    `${basePath}/${selectionId}/supplies`,
    {
      axiosConfig: {
        params,
      },
      queryConfig: {
        enabled: !!selectionId,
      },
    }
  )

export const useInfiniteAPIGetSelectionCandidates = (
  selectionId: ActiveSelection['id'],
  params: ParamsGetSelectionCandidates
) =>
  useInfiniteQueryApi<ResponseGetPSSelectionCandidates>(
    `${basePath}/${selectionId}/supplies`,
    {
      axiosConfig: {
        params,
      },
      queryConfig: {
        enabled: !!selectionId,
        getNextPageParam: ({ data, meta: { page, pageSize } }) => {
          if (!data.length || data.length < params.pageSize) return

          return {
            page: page + 1,
            pageSize,
          }
        },
      },
    }
  )

interface ParamsDownloadSelectedCandidates {
  downloadType: 'SELECTED' | 'ALL'
}

export const apiDownloadSelectedCandidates = (
  selectionId: ActiveSelection['id'],
  params: ParamsDownloadSelectedCandidates
) =>
  apiFileDownload(
    `${baseAPIPathGuruKS}/v1/ps/selection/${selectionId}/candidates/download`,
    {
      params,
    }
  )

interface RequestSelectPSCandidate {
  candidateId: PSSelectionCandidate['candidateId']
}

export const useAPISelectPSCandidate = (selectionId: ActiveSelection['id']) =>
  useMutationApi<{ message: string }, Error, RequestSelectPSCandidate>(
    ({ candidateId }: RequestSelectPSCandidate) =>
      `${basePath}/${selectionId}/select/${candidateId}`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )

export const useAPIUnselectPSCandidate = (selectionId: ActiveSelection['id']) =>
  useMutationApi<{ message: string }, Error, RequestSelectPSCandidate>(
    ({ candidateId }: RequestSelectPSCandidate) =>
      `${basePath}/${selectionId}/unselect/${candidateId}`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )

export interface ResponseGetSK {
  selectionId: string
  skFileurl: string
  skFilename: string
  skNumber: string
  dutyStartDate: string
  dutyLetterNumber: string
}

export const useAPIGetSK = (selectionId: ActiveSelection['id']) =>
  useQueryApi<ResponseGetSK>(`${basePath}/${selectionId}/surat-keputusan`, {
    queryConfig: {
      enabled: !!selectionId,
    },
  })

export const useAPIUploadSK = (selectionId: ActiveSelection['id']) =>
  useMutationApi<{ message: string }, Error, FormData>(
    `${basePath}/${selectionId}/surat-keputusan`,
    {
      axiosConfig: {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

export const useAPIFinalizeSelection = (selectionId: ActiveSelection['id']) =>
  useMutationApi<{ message: string }, Error, any>(
    `${basePath}/${selectionId}/finalize`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )

export interface ParamsGetAllocationOverview {
  regionId: string
  regionType: RegionType
}

export interface ResponseGetAllocationOverview {
  activeCount: number
  satdikCount: number
  currentRatio: number
  suggestedRatio: number
  suggestedRatioBeforeIkg: number
  ikg: number
  calculationDetailResult: number
  currentAhliMudaNumber: number
  currentAhliMadyaNumber: number
  currentAhliUtamaNumber: number
  suggestedAhliNumberTotal: number
  suggestedAhliMudaNumber: number
  suggestedAhliMadyaNumber: number
  suggestedAhliUtamaNumber: number
}

export const useAPIGetAllocationOverview = (
  params: ParamsGetAllocationOverview
) =>
  useQueryApi<ResponseGetAllocationOverview>(
    `${basePath}/allocation-overview`,
    {
      axiosConfig: {
        params,
      },
    }
  )

export interface ParamsGetRequestAllocationApproval {
  regionId: string
  regionType: RegionType
}

export interface ResponseGetRequestAllocationApproval {
  status: 'NO_REQUEST' | 'WAITING_APPROVAL' | 'APPROVED' | 'REJECTED'
}

export const useAPIGetRequestAllocationApproval = (
  params: ParamsGetRequestAllocationApproval
) =>
  useQueryApi<ResponseGetRequestAllocationApproval>(
    `${basePath}/request-allocation-approval`,
    {
      axiosConfig: {
        params,
      },
      queryConfig: {
        staleTime: 1000,
      },
    }
  )

export interface ReqBodyRequestAllocationApproval {
  regionId: string
  regionType: RegionType
  ahliMudaNumber: number
  ahliMadyaNumber: number
  ahliUtamaNumber: number
  reason: string
}

export const useAPIRequestAllocationApproval = () =>
  useMutationApi<{ message: string }, Error, ReqBodyRequestAllocationApproval>(
    `${basePath}/request-allocation-approval`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )

export interface ParamsGetAllocation {
  regionId: string
  regionType: RegionType
}

export interface ResponseGetAllocation {
  ahliNumberTotal: number
  ahliMudaNumber: number
  ahliMadyaNumber: number
  ahliUtamaNumber: number
  reason: string
}

export const useAPIGetAllocation = (params: ParamsGetAllocation) =>
  useQueryApi<ResponseGetAllocation>(`${basePath}/allocation`, {
    axiosConfig: {
      params,
    },
  })
